// Owners cell (seven-column tracker): account handler + legal owner in one cell — click to change either.
import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getAgeingHandlers, setLegalCaseFlags, sortNames, updateAgeingCase } from "@/lib/api";

let legalRosterCache = null;

export function OwnersCell({ c, accountHandlers = [], onChanged }) {
  const [open, setOpen] = useState(false);
  const [legalRoster, setLegalRoster] = useState(legalRosterCache || []);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (!open || legalRosterCache) return;
    getAgeingHandlers("legal").then((r) => { legalRosterCache = sortNames(r.handlers || []); setLegalRoster(legalRosterCache); }).catch(() => {});
  }, [open]);
  const save = async (kind, value) => {
    setBusy(kind);
    try {
      if (kind === "account") await updateAgeingCase(c.case_id, { handler: value || null });
      else await setLegalCaseFlags(c.case_id, { legal_handler: value || null });
      toast.success(`${c.customer_name || c.loan_application_id} · ${kind === "account" ? "account handler" : "legal owner"} → ${value || "unassigned"}`);
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const accountOptions = sortNames([...new Set([...(accountHandlers || []), c.handler].filter(Boolean))]);
  const legalOptions = sortNames([...new Set([...legalRoster, c.legal_handler].filter(Boolean))]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="text-left text-xs leading-tight hover:bg-slate-100 rounded px-1 -mx-1 py-0.5 w-full" onClick={(e) => e.stopPropagation()} data-testid={`legal-owners-${c.case_id}`} title="Account handler · legal owner — click to change">
          <div className="truncate" data-testid={`legal-account-handler-${c.case_id}`}>{c.handler || <span className="text-muted-foreground italic">No account owner</span>}</div>
          <div className="truncate" data-testid={`legal-owner-${c.case_id}`}>
            {c.legal_handler ? <span className="text-red-800">⚖ {c.legal_handler}</span> : <span className="text-amber-700 italic">⚖ no legal owner</span>}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3 space-y-2 text-xs" onClick={(e) => e.stopPropagation()} data-testid={`legal-owners-popover-${c.case_id}`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Account handler (Collections)</div>
          <select className="w-full h-8 border rounded px-2 bg-white" value={c.handler || ""} disabled={!!busy} onChange={(e) => save("account", e.target.value)} data-testid={`legal-owners-account-select-${c.case_id}`}>
            <option value="">— unassigned —</option>
            {accountOptions.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Legal owner</div>
          <select className="w-full h-8 border rounded px-2 bg-white" value={c.legal_handler || ""} disabled={!!busy} onChange={(e) => save("legal", e.target.value)} data-testid={`legal-owners-legal-select-${c.case_id}`}>
            <option value="">— no legal owner —</option>
            {legalOptions.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        {busy && <div className="flex items-center gap-1 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}
      </PopoverContent>
    </Popover>
  );
}
